import { ParamMap } from '@angular/router';

/**
 * A flight chosen in the BuuPass AI chat on buupass.com. Its "Book Now" links to
 * /flights/booking/... with these params so this page can skip straight to payments.
 */
export interface AiFlightSelection {
  resultId: string | null;
  offerId: string | null;
  /** Marketing carrier IATA code of the first outbound segment, e.g. "KQ". */
  carrier: string | null;
  /** Local departure, "YYYY-MM-DDTHH:mm" (any trailing seconds/offset is ignored). */
  departure: string | null;
  fare: number | null;
}

export const AI_SELECTION_PARAMS = ['ai_result', 'ai_offer', 'ai_carrier', 'ai_departure', 'ai_fare'];

export function readAiFlightSelection(params: ParamMap): AiFlightSelection | null {
  const text = (key: string): string | null => params.get(key)?.trim() || null;
  const fare = Number(text('ai_fare'));
  const selection: AiFlightSelection = {
    resultId: text('ai_result'),
    offerId: text('ai_offer'),
    carrier: text('ai_carrier')?.toUpperCase() ?? null,
    departure: toMinute(text('ai_departure')),
    fare: Number.isFinite(fare) && fare > 0 ? fare : null,
  };
  const identifiable = selection.resultId || selection.offerId || (selection.carrier && selection.departure);
  return identifiable ? selection : null;
}

/**
 * The offer matching the AI selection: the same result/offer id if the search
 * returned it again, otherwise the same carrier departing at the same minute
 * (closest fare first, since prices move between searches).
 */
export function findAiSelectedFlight(flights: any[], selection: AiFlightSelection): any | null {
  if (!Array.isArray(flights) || flights.length === 0) {
    return null;
  }

  const byId = flights.find(f =>
    (selection.resultId && f?.result_id === selection.resultId) ||
    (selection.offerId && f?.offer_id === selection.offerId));
  if (byId) {
    return byId;
  }

  if (!selection.carrier || !selection.departure) {
    return null;
  }
  const sameFlight = flights.filter(f => {
    const segment = f?.slices?.[0]?.segments?.[0];
    return segment?.marketing_carrier?.iata_code?.toUpperCase() === selection.carrier &&
      toMinute(segment?.departing_at) === selection.departure;
  });
  if (selection.fare === null || sameFlight.length < 2) {
    return sameFlight[0] ?? null;
  }
  const fareGap = (f: any): number => Math.abs(Number(f?.total_amount) - (selection.fare as number));
  return [...sameFlight].sort((a, b) => fareGap(a) - fareGap(b))[0];
}

function toMinute(value: string | null | undefined): string | null {
  const match = /^(\d{4}-\d{2}-\d{2})[T ](\d{2}:\d{2})/.exec(value ?? '');
  return match ? `${match[1]}T${match[2]}` : null;
}
